import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import LeadForm from "../components/LeadForm";
import PageMeta from "../components/PageMeta";
import { PRODUCTS, CATEGORIES } from "../data/content";

const Products = () => {
  const [query, setQuery] = useState("");
  const [quoteFor, setQuoteFor] = useState(null);
  const [params, setParams] = useSearchParams();
  const active = params.get("category") || "all";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PRODUCTS.filter(
      (p) =>
        (active === "all" || p.category === active) &&
        (!q || `${p.name} ${p.short_description} ${p.category}`.toLowerCase().includes(q))
    );
  }, [active, query]);

  const selectCategory = (slug) => {
    setParams(slug && slug !== "all" ? { category: slug } : {}, { replace: true });
  };

  return (
    <main data-testid="products-page">
      <PageMeta title="Products | Hospital Equipment, Furniture & Consumables — AccuSurge" description="Browse AccuSurge's catalogue of hospital furniture, critical care, operation theatre, diagnostic equipment, dialysis and consumables. Request a quote online." />
      <section className="bg-brand-950 pb-16 pt-40 md:pb-20 grain relative overflow-hidden">
        <div className="container-x relative">
          <Reveal>
            <span className="eyebrow text-accent">Product Catalogue</span>
            <h1 className="mt-6 max-w-3xl font-heading text-4xl md:text-6xl font-semibold tracking-tighter text-white">
              Medical Equipment & Healthcare Products.
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-white/70">
              Supporting evidence for every project we coordinate. Prices are provided on request — share your
              requirement and our team will respond with suitable options.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="sticky top-[76px] z-30 border-b border-slate-200 bg-white/90 py-4 backdrop-blur-xl" data-testid="products-filter-bar">
        <div className="container-x flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 items-center gap-6">
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                className="field !pl-10"
                placeholder="Search products…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="product-search-input"
                aria-label="Search products"
              />
            </div>
            <span className="hidden text-xs text-slate-400 md:block" data-testid="product-count">
              {filtered.length} product{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="flex gap-2 overflow-x-auto [scrollbar-width:none]" role="tablist" aria-label="Product categories">
            {[{ slug: "all", name: "All" }, ...CATEGORIES].map((c) => (
              <button
                key={c.slug}
                role="tab"
                aria-selected={active === c.slug}
                onClick={() => selectCategory(c.slug)}
                className={`shrink-0 rounded-full px-4 py-2 text-[13px] font-semibold transition-colors ${
                  active === c.slug ? "bg-brand-600 text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-brand-300"
                }`}
                data-testid={`category-pill-${c.slug}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-x">
          {filtered.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-16 text-center" data-testid="products-empty">
              <p className="font-heading text-lg font-medium text-slate-500">No products match your search.</p>
              <p className="mt-2 text-sm text-slate-400">Try a different term or category — or ask us; we coordinate sourcing beyond the listed catalogue.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map((p) => (
                <article key={p.id} className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)]" data-testid={`product-card-${p.slug}`}>
                  <Link to={`/products/${p.slug}`} className="h-48 overflow-hidden">
                    <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                  </Link>
                  <div className="flex flex-1 flex-col p-5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent-600">
                      {CATEGORIES.find((c) => c.slug === p.category)?.name || p.category.replace(/-/g, " ")}
                    </span>
                    <h2 className="mt-2 font-heading text-lg font-semibold leading-snug text-ink">
                      <Link to={`/products/${p.slug}`}>{p.name}</Link>
                    </h2>
                    <p className="mt-2 line-clamp-2 flex-1 text-[13px] leading-relaxed text-slate-500">{p.short_description}</p>
                    <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
                      <button
                        onClick={() => setQuoteFor(p)}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 transition-colors hover:text-accent-600"
                        data-testid={`request-price-${p.slug}`}
                      >
                        Request Price <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                      <Link to={`/products/${p.slug}`} className="text-xs font-medium text-slate-400 hover:text-brand-700">Details</Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {quoteFor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="quote-modal">
          <div className="absolute inset-0 bg-brand-950/70 backdrop-blur-sm" onClick={() => setQuoteFor(null)} />
          <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-8 shadow-2xl">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <span className="eyebrow">Request a Quote</span>
                <h2 className="mt-2 font-heading text-2xl font-semibold text-ink">{quoteFor.name}</h2>
              </div>
              <button onClick={() => setQuoteFor(null)} className="rounded-full border border-slate-200 p-2 text-slate-500 hover:text-ink" aria-label="Close quote form" data-testid="quote-modal-close">✕</button>
            </div>
            <LeadForm variant="quote" defaults={{ product_name: quoteFor.name }} compact />
          </div>
        </div>
      )}
    </main>
  );
};

export default Products;
